import React, { useState, useEffect } from 'react'
import { number, oneOfType, string } from 'prop-types'
import { css } from '@emotion/core'
import * as Yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button, DialogOverlay, Row, useOverlay, Paragraph } from '@fs/zion-ui'
import ZionForm, { FormAutosuggest, useZionForm } from '@fs/zion-form'
import { useUser } from '@fs/zion-user'
import { getAnonSession } from '@fs/zion-session'
import { useTranslation } from 'react-i18next'
import { useRegionLanguage } from '../../hooks/hooks'
import { useDSCDispatch, ACTIONS } from '../../../../state/DSCStore'

const separatorStyle = css`
  font-size: 12px;
  text-align: center;
  max-width: 100%;
  color: #acacac;
  position: relative;
  ::before {
    content: '';
    display: block;
    width: 220px;
    height: 1px;
    background: #acacac;
    position: absolute;
    inset-inline-start: 0;
    top: 50%;
    z-index: -2;
  }
  ::after {
    content: '';
    display: block;
    width: 220px;
    height: 1px;
    background: #acacac;
    position: absolute;
    inset-inline-end: 0;
    top: 50%;
    z-index: -2;
  }
  span {
    background: #fff;
  }
`

export default function LanguageWarningDialog({ language, region, year }) {
  const [isValidLangOption, setValidLangOption] = useState(true)
  const user = useUser()
  const overlay = useOverlay()
  const openOverlay = overlay.open
  const closeOverlay = overlay.close
  const { t } = useTranslation()
  const countryLanguageOptions = useRegionLanguage(t)
  const dispatch = useDSCDispatch()

  useEffect(() => {
    if (region && language && countryLanguageOptions.length !== 0) {
      const foundValidLangOption = countryLanguageOptions.some((option) => option.key === `${region}_${language}`)
      if (!foundValidLangOption) {
        setValidLangOption(false)
      }
    }
  }, [countryLanguageOptions, language, region])

  useEffect(() => {
    if (!isValidLangOption) {
      openOverlay()
    }
  }, [isValidLangOption, openOverlay])

  const validationSchema = Yup.object().shape({
    modifiedValue: Yup.object().required(
      t('aam-landing-validation-select-country-language', 'Please select a valid country and language')
    ),
  })

  const methods = useZionForm({
    mode: 'all',
    resolver: yupResolver(validationSchema),
    defaultValues: {
      modifiedValue: undefined,
    },
  })

  const {
    reset,
    formState: { isValid },
  } = methods

  const passToGlobal = (values) => {
    const nextValues = { ...values, languageRegion: values.modifiedValue }

    if (user?.contactName) {
      const names = user.contactName.split(' ')
      nextValues.firstName = names[0]
      nextValues.lastName = names[1]
      nextValues.birthYear = year
    }

    reset()
    getAnonSession()
      .then(() => {
        dispatch({
          type: ACTIONS.POPULATE_NONSTANDARD_USER,
          data: { nonstandardUser: nextValues },
        })
      })
      .then(() => {
        closeOverlay()
        globalThis.location.reload()
      })
  }

  const suppressAutocomplete = (event) => {
    event.target.setAttribute('autocomplete', 'disabled')
  }

  return (
    <DialogOverlay {...overlay} title={t('aam.country-language-oops')}>
      <Paragraph size="sm">
        <span css={{ color: '#b81f1e' }}>
          {t(`global.countries.country${region.toUpperCase()}`)} / {t(`global.countries.language.${language}`)}
        </span>
      </Paragraph>
      <Paragraph size="sm">{t('aam.country-language-warning-message')}</Paragraph>
      <div css={separatorStyle}>
        <span>{t('aam.country-language-separator', 'OR')}</span>
      </div>
      <Paragraph size="sm">{t('aam.country-language-warning-ignore-message')}</Paragraph>
      <ZionForm methods={methods} onSubmit={passToGlobal}>
        <>
          <FormAutosuggest
            suggestions={countryLanguageOptions}
            name="modifiedValue"
            inputProps={{
              onFocus: suppressAutocomplete,
            }}
            label={t('aam-landing-validation-select-country-language')}
            placeholder={t('aam-landing-validation-select-country-language')}
            highlightMatchingText
          />
          <Row>
            <Button
              data-testid="language-warning-dialog-submit-button"
              type="submit"
              external="true"
              emphasis="high"
              disabled={!isValid}
            >
              {t('common-ui:submit.action', 'Submit')}
            </Button>
            <Button data-testid="language-warning-dialog-cancel-button" onClick={closeOverlay}>
              {' '}
              {t('common-ui:cancel.action', 'Cancel')}
            </Button>
          </Row>
        </>
      </ZionForm>
    </DialogOverlay>
  )
}

LanguageWarningDialog.propTypes = {
  language: string,
  region: string,
  year: oneOfType([string, number]),
}
